import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Copy, RefreshCw, Users, GraduationCap, Briefcase, CheckCircle2, XCircle,
  Loader2, Search, User, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  Calendar as CalendarIcon, Clock, MapPin, AlertCircle, Eye, KeyRound, X, FileText,
  LayoutDashboard, Settings, Menu, ArrowRight, UserCheck, ShieldCheck, Link2,
  Building2, Ban, RotateCcw, History, ExternalLink, Sparkles,
  Video, BriefcaseBusiness, Presentation, Compass, Star, Award, ArrowUpDown, LifeBuoy, LogOut, Home,
} from 'lucide-react';
import { toast } from 'sonner';
import AssignMentorDialog, { isOfflineMode, formatDay, formatTime, venueText } from './coordinator/AssignMentorDialog';
import InternshipsSection from './coordinator/InternshipsSection';
import ExperienceTable from './coordinator/ExperienceTable';
import ExperienceDetailsDialog from './coordinator/ExperienceDetailsDialog';
import FeedbackReminderButton from './coordinator/FeedbackReminderButton';
import SchoolCalendar from './coordinator/SchoolCalendar';
import { PARTICIPATION_AGREEMENT, SAFEGUARDING_AGREEMENT } from '@/data/agreements';
import { AgreementModal } from '@/components/AgreementModal';
import { OverviewTab, SchoolSettingsTab, InternshipListExtras } from './coordinator/OverviewAndSettings';
import { RecognizeMentorDialog } from './coordinator/RecognizeMentorDialog';
import { StudentsTab } from './coordinator/StudentsManager';
import AskParentsDialog from './coordinator/AskParentsDialog';
import CareerCoverage from './coordinator/CareerCoverage';
import RequestsAndIssues from './coordinator/RequestsAndIssues';
import SafeguardingSection from './coordinator/SafeguardingSection';
import CoordinatorAcknowledgments from '@/components/CoordinatorAcknowledgments';
import ReportIssueButton from '@/components/ReportIssueButton';
import { buildAttention, needsMentorConfirmation, needsFamilyFeedback, isActionable } from './coordinator/attention';
import { useOverflowFade } from '@/lib/useOverflowFade';

// ─── Status badge colors ───

// ============ Reschedule Requests Tab ============
function RescheduleRequestsTab({ mode, onRefresh }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rescheduleBooking, setRescheduleBooking] = useState(null);
  const [decliningId, setDecliningId] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const offlineSection = mode === 'offline';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/coordinator/reschedule-requests');
      setRequests((r.data || []).filter(b => isOfflineMode(b.assigned_mode || b.requested_mode) === offlineSection));
    } catch { setRequests([]); }
    setLoading(false);
  }, [offlineSection]);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    setApprovingId(id);
    try {
      await api.put(`/coordinator/bookings/${id}/reschedule-request/approve`);
      toast.success('Approved — the family has been asked to confirm the new time');
      load(); onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not approve this time'); }
    setApprovingId(null);
  };

  const decline = async (id) => {
    setDecliningId(id);
    try {
      await api.put(`/coordinator/bookings/${id}/reschedule-request/decline`, { note: null });
      toast.success('Request declined — the original schedule stands');
      load(); onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not decline the request'); }
    setDecliningId(null);
  };

  if (loading) return <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[#9CA3AF]" /></div>;

  return (
    <div data-testid="reschedule-requests-tab">
      {requests.length === 0 ? (
        <p className="text-center py-12 text-[#9CA3AF] text-sm">No open reschedule requests.</p>      ) : (
        <div className="space-y-2">
          {requests.map(b => (
            <div key={b.id} className="bg-white rounded-xl border border-[#E5E7EB] px-4 py-3" data-testid={`reschedule-row-${b.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#1F2937]">{b.career_name}</p>
                  <p className="text-sm text-[#6B7280] mt-0.5">{b.child_name} — {b.parent_name}</p>
                  <p className="text-sm text-[#4B5563] mt-1.5">
                    <span className="text-[#9CA3AF]">{['awaiting_reschedule', 'waitlisted'].includes(b.status) ? 'Was: ' : 'Now: '}</span>
                    {formatDay(b.reschedule_request?.from_date || b.assigned_date)}
                    {b.assigned_time_slot ? ` at ${formatTime(b.assigned_time_slot)}` : ''}
                    {b.mentor_name ? ` · ${b.mentor_name}` : ''}
                  </p>
                  <p className="text-xs text-violet-700 mt-0.5" data-testid={`reason-${b.id}`}>
                    <span className="text-[#9CA3AF]">Reason: </span>"{b.reschedule_request?.reason}"
                  </p>
                  <p className="text-sm text-[#4B5563] mt-0.5" data-testid={`proposal-${b.id}`}>
                    <span className="text-[#9CA3AF]">Time found: </span>
                    {b.reschedule_proposal
                      ? <>{formatDay(b.reschedule_proposal.assigned_date)} at {formatTime(b.reschedule_proposal.assigned_time_slot)} · {b.reschedule_proposal.mentor_name}</>
                      : <span className="text-[#9CA3AF]">nothing suitable yet — place a slot by hand</span>}
                  </p>
                  {['awaiting_reschedule', 'waitlisted'].includes(b.status) && (
                    <p className="text-[11px] text-[#9CA3AF] mt-1" data-testid={`awaiting-note-${b.id}`}>
                      That slot is released. The experience stays active until you place a new time.
                    </p>
                  )}
                </div>
                <Badge className="border-0 text-[10px] bg-violet-50 text-violet-600 shrink-0">
                  {b.status === 'awaiting_reschedule' ? 'Needs new slot' : 'Reschedule'}
                </Badge>
              </div>
              <div className="flex gap-2 mt-3">
                {b.reschedule_proposal && (
                  <Button size="sm" className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-lg text-xs" disabled={approvingId === b.id} onClick={() => approve(b.id)} data-testid={`approve-reschedule-${b.id}`}>
                    {approvingId === b.id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}Approve This Time
                  </Button>
                )}
                <Button size="sm" variant="outline" className="rounded-lg text-xs" onClick={() => setRescheduleBooking(b)} data-testid={`assign-reschedule-${b.id}`}>
                  Choose Another Slot
                </Button>
                {['confirmed', 'pending_parent_confirmation'].includes(b.status) && (
                  <Button size="sm" variant="outline" className="rounded-lg text-xs" disabled={decliningId === b.id} onClick={() => decline(b.id)} data-testid={`decline-reschedule-${b.id}`}>
                    {decliningId === b.id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}Keep Original
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {rescheduleBooking && (
        <AssignMentorDialog
          mode="reschedule"
          booking={rescheduleBooking}
          onClose={() => setRescheduleBooking(null)}
          onAssigned={() => { setRescheduleBooking(null); load(); onRefresh(); }}
        />
      )}
    </div>
  );
}

// ============ Mentor Applications Tab ============
const MENTOR_COLUMNS = [
  { key: 'parent_name', label: 'Mentor', align: 'left', cls: '' },
  { key: 'profession_title', label: 'Profession', align: 'left', cls: 'hidden md:table-cell' },
  { key: 'children', label: 'Children', align: 'left', cls: 'hidden lg:table-cell' },
  { key: 'completed', label: 'Completed', align: 'center', cls: '', extra: ' Mentoring Experiences' },
  { key: 'students', label: 'Students', align: 'center', cls: 'hidden sm:table-cell', extra: ' Reached' },
  { key: 'rating', label: 'Rating', align: 'center', cls: '' },
  { key: 'level', label: 'Mentor Level', align: 'left', cls: 'hidden lg:table-cell' },
  { key: 'approval_status', label: 'Status', align: 'left', cls: '' },
];

/** Highest recognition the mentor has actually achieved, or null. */
const mentorLevel = (p) => {
  const recs = p.recognitions || [];
  return recs.length ? recs[recs.length - 1] : null;
};

const childLabel = (c) => {
  const cls = [c.grade, c.division].filter(Boolean).join('-');
  return `${c.name}${cls ? ` (${cls})` : ''}`;
};

const childrenLine = (p) => (p.children || []).map(childLabel);

const mentorSortValue = (p, key) => {
  const im = p.impact || {};
  switch (key) {
    case 'parent_name': return (p.parent_name || p.profession_title || '').toLowerCase();
    case 'profession_title': return (p.profession_title || '').toLowerCase();
    case 'children': return (p.children || []).length;
    case 'completed': return im.completed_experiences || 0;
    case 'students': return im.students_reached || 0;
    case 'rating': return im.rating ? Number(im.rating) : null;
    case 'level': return mentorLevel(p)?.threshold || 0;
    case 'approval_status': return p.approval_status || '';
    default: return '';
  }
};

function MentorApplicationsTab() {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [recognize, setRecognize] = useState(null);
  const [sort, setSort] = useState({ key: 'completed', dir: -1 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/coordinator/mentors');
      setProfiles(r.data);
      if (!r.data.some(p => p.approval_status === 'pending')) setFilter('all');
    } catch {}
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = profiles.filter(p => {
    if (filter !== 'all' && p.approval_status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(p.parent_name || '').toLowerCase().includes(q) && !(p.profession_title || '').toLowerCase().includes(q) && !(p.company_name || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // Default order: most experiences, then best rated (unrated last), then name
  const sorted = [...filtered].sort((a, b) => {
    const av = mentorSortValue(a, sort.key), bv = mentorSortValue(b, sort.key);
    if (av === null || bv === null) {
      if (av !== bv) return av === null ? 1 : -1;        // unrated always last
    } else if (av !== bv) {
      return (av > bv ? 1 : -1) * sort.dir;
    }
    const ac = (a.impact || {}).completed_experiences || 0, bc = (b.impact || {}).completed_experiences || 0;
    if (ac !== bc) return bc - ac;
    const ar = (a.impact || {}).rating ? Number(a.impact.rating) : null;
    const br = (b.impact || {}).rating ? Number(b.impact.rating) : null;
    if (ar !== br) {
      if (ar === null) return 1;
      if (br === null) return -1;
      return br - ar;
    }
    return (a.parent_name || '').localeCompare(b.parent_name || '');
  });

  const toggleSort = (key) => setSort(s => (
    s.key === key ? { key, dir: -s.dir } : { key, dir: ['parent_name', 'profession_title', 'approval_status'].includes(key) ? 1 : -1 }
  ));

  const statusColors = {
    pending: 'bg-amber-50 text-amber-700', approved: 'bg-emerald-50 text-emerald-600',
    rejected: 'bg-red-50 text-red-600', draft: 'bg-gray-100 text-gray-500',
    suspended: 'bg-orange-50 text-orange-700', deactivated: 'bg-slate-100 text-slate-600',
  };

  return (
    <div data-testid="mentor-applications-tab">
      <RecognizeMentorDialog
        mentor={recognize}
        open={!!recognize}
        onOpenChange={(v) => { if (!v) setRecognize(null); }}
        onSent={load}
      />
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="flex gap-1.5 flex-nowrap overflow-x-auto sm:flex-wrap [&::-webkit-scrollbar]:hidden">
          {['all', 'pending', 'approved', 'suspended', 'rejected', 'deactivated', 'draft'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${filter === f ? 'bg-[#F97D6A] text-white' : 'bg-white border border-[#E5E7EB] text-[#6B7280] hover:border-[#F97D6A]'}`}
              data-testid={`mentor-filter-${f}`}>
              {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)} ({f === 'all' ? profiles.length : profiles.filter(p => p.approval_status === f).length})
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-60">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9CA3AF]" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search mentors..." className="pl-9 h-9 rounded-lg text-sm border-[#E5E7EB]" data-testid="mentor-search" />
        </div>
      </div>

      {loading ? <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[#9CA3AF]" /></div>
        : sorted.length === 0 ? <p className="text-center py-12 text-[#9CA3AF] text-sm">No mentor applications matching this filter.</p>
        : (
          <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-x-auto">
            <table className="w-full sm:min-w-[620px] text-sm" data-testid="mentors-table">
              <thead className="bg-[#F8F9FA]">
                <tr>
                  {MENTOR_COLUMNS.map(c => (
                    <th key={c.key} className={`px-3 py-2.5 text-${c.align} text-xs font-semibold text-[#6B7280] ${c.cls}`}>
                      <button type="button" onClick={() => toggleSort(c.key)}
                        className="inline-flex items-center gap-1 hover:text-[#1F2937] transition-colors"
                        data-testid={`mentor-sort-${c.key}`}>
                        {c.label}{c.extra && <span className="hidden lg:inline">{c.extra}</span>}
                        <ArrowUpDown className={`h-3 w-3 ${sort.key === c.key ? 'text-[#F97D6A]' : 'text-[#D1D5DB]'}`} />
                      </button>
                    </th>
                  ))}
                  <th className="px-3 py-2.5 text-right text-xs font-semibold text-[#6B7280] w-24">View</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(p => {
                  const im = p.impact || {};
                  const level = mentorLevel(p);
                  const kids = childrenLine(p);
                  return (
                    <tr key={p.id} onClick={() => navigate(`/coordinator/mentors/${p.id}`)} className="group border-t border-[#F3F4F6] hover:bg-[#FFF8F6] cursor-pointer transition-colors" data-testid={`mentor-${p.id}`}>
                      <td className="px-3 py-2.5">
                        <p className="font-medium text-[#1F2937] md:whitespace-nowrap">{p.parent_name || p.profession_title}</p>
                        <p className="text-[10px] text-[#9CA3AF] md:hidden">{p.profession_title}</p>
                        <p className="text-[10px] text-[#6B7280] lg:hidden" data-testid={`mentor-children-inline-${p.id}`}>
                          {kids.length ? kids.join(', ') : '—'}
                        </p>
                        {level && <p className="text-[10px] font-semibold lg:hidden" style={{ color: level.color }}>{level.title}</p>}
                        {p.recognition_ready && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setRecognize(p); }}
                            className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold text-[#B87333] bg-[#FBF2E9] hover:bg-[#F6E6D4] rounded-full px-2 py-0.5 transition-colors"
                            data-testid={`recognition-ready-${p.id}`}
                          >
                            <Award className="h-3 w-3" />
                            Recognition Ready{p.recognition_pending.length > 1 ? ` · ${p.recognition_pending.length}` : ''}
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-[#4B5563] hidden md:table-cell">
                        {p.profession_title}
                        {p.company_name && <span className="block text-[10px] text-[#9CA3AF]">{p.company_name}</span>}
                      </td>
                      <td className="px-3 py-2.5 text-[#4B5563] hidden lg:table-cell" data-testid={`mentor-children-${p.id}`}>
                        {kids.length === 0 ? <span className="text-[#9CA3AF]">—</span>
                          : kids.map(k => <span key={k} className="block whitespace-nowrap">{k}</span>)}
                      </td>
                      <td className="px-3 py-2.5 text-center" data-testid={`mentor-completed-${p.id}`}>
                        <span className={im.completed_experiences ? 'font-semibold text-[#1F2937]' : 'text-[#9CA3AF]'}>{im.completed_experiences || 0}</span>
                      </td>
                      <td className="px-3 py-2.5 text-center hidden sm:table-cell" data-testid={`mentor-students-${p.id}`}>
                        <span className={im.students_reached ? 'font-semibold text-[#1F2937]' : 'text-[#9CA3AF]'}>{im.students_reached || 0}</span>
                      </td>
                      <td className="px-3 py-2.5 text-center" data-testid={`mentor-rating-${p.id}`}>
                        {im.rating ? (
                          <span className="inline-flex items-center gap-1 text-[#1F2937] font-medium">
                            <Star className="h-3.5 w-3.5 text-[#F5A623] fill-[#F5A623]" />{im.rating}
                            <span className="text-[10px] text-[#9CA3AF]">({im.rating_count})</span>
                          </span>
                        ) : <span className="text-[#9CA3AF]">—</span>}
                      </td>
                      <td className="px-3 py-2.5 hidden lg:table-cell whitespace-nowrap" data-testid={`mentor-level-${p.id}`}>
                        {level ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: level.color }}>
                            <Award className="h-3.5 w-3.5" />{level.title}
                          </span>
                        ) : <span className="text-[#9CA3AF]">—</span>}
                      </td>
                      <td className="px-3 py-2.5"><Badge className={`border-0 text-[10px] ${statusColors[p.approval_status] || 'bg-gray-100 text-gray-500'}`}>{p.approval_status}</Badge></td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">
                        {p.recognition_ready && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setRecognize(p); }}
                            className="inline-flex items-center justify-center h-7 w-7 rounded-lg text-[#B87333] hover:bg-[#FBF2E9] transition-colors mr-1"
                            title="Recognise mentor"
                            aria-label={`Recognise ${p.parent_name}`}
                            data-testid={`mentor-recognize-${p.id}`}
                          >
                            <Award className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); navigate(`/coordinator/mentors/${p.id}`); }}
                          className="inline-flex items-center justify-center h-7 w-7 rounded-lg text-[#9CA3AF] group-hover:text-[#F97D6A] hover:bg-[#FFF0ED] transition-colors"
                          title="View mentor details"
                          aria-label={`View details for ${p.parent_name || p.profession_title}`}
                          data-testid={`mentor-view-${p.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

    </div>
  );
}

// ============ Unfulfilled Requests Tab ============
function UnfulfilledRequestsTab({ mode }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [askBooking, setAskBooking] = useState(null);
  const offlineSection = mode === 'offline';

  const load = useCallback(() => {
    api.get('/coordinator/unfulfilled-requests')
      .then(r => setRequests((r.data || [])
        .map(g => ({ ...g, students: (g.students || []).filter(st => isOfflineMode(st.requested_mode) === offlineSection) }))
        .filter(g => g.students.length)
        .map(g => ({ ...g, student_count: g.students.length }))))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [offlineSection]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[#9CA3AF]" /></div>;

  return (
    <div data-testid="unfulfilled-requests-tab">
      {requests.length === 0 ? <p className="text-center py-12 text-[#9CA3AF] text-sm">No unfulfilled {offlineSection ? 'workplace visit' : 'online session'} requests.</p> : (
        <div className="space-y-2">
          {requests.map(r => (
            <div key={r.career_slug} className="bg-white rounded-xl border border-[#E5E7EB] px-4 py-3" data-testid={`unfulfilled-${r.career_slug}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#1F2937]">{r.career_name || 'Career Experience'}</p>
                  <p className="text-sm text-[#6B7280] mt-0.5">
                    {r.student_count} student{r.student_count === 1 ? '' : 's'} waiting
                    {(r.reasons || []).includes('no_mentor') ? ' · no approved mentor for this career' : ''}
                    {(r.reasons || []).includes('no_offline_visit') ? ' · no upcoming workplace visit' : ''}
                    {(r.reasons || []).includes('no_online_availability') ? ' · no online availability set' : ''}
                    {(r.reasons || []).includes('capacity_full') ? ' · mentor capacity full' : ''}
                  </p>
                </div>
                <Badge className="border-0 text-[10px] bg-amber-50 text-amber-700 shrink-0">Unfulfilled</Badge>
              </div>
              <div className="mt-2 space-y-1">
                {(r.students || []).map(st => {
                  const asks = st.asks || [];
                  const accepted = asks.filter(a => a.status === 'accepted');
                  return (
                    <div key={st.booking_id} className="bg-[#F8F9FA] rounded-lg px-3 py-2" data-testid={`unfulfilled-row-${st.booking_id}`}>
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm text-[#1F2937]">
                          {st.child_name}{st.child_grade ? ` · Grade ${st.child_grade}` : ''}
                          <span className="text-[#9CA3AF]"> — {st.parent_name}</span>
                        </p>
                        <span className="text-[10px] text-[#9CA3AF] shrink-0">
                          {st.created_at ? new Date(st.created_at).toLocaleDateString('en-GB') : ''}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-3 mt-1">
                        <p className="text-[11px] text-[#6B7280]" data-testid={`ask-status-${st.booking_id}`}>
                          {accepted.length > 0
                            ? `${accepted.map(a => a.parent_name || a.parent_email).join(', ')} offered to help — taken into mentor signup`
                            : asks.length > 0
                            ? `Asked ${asks.length} parent${asks.length === 1 ? '' : 's'}: ${asks.map(a => a.parent_name || a.parent_email).join(', ')}`
                            : 'No parent with this profession on record'}
                        </p>
                        <Button variant="outline" size="sm" onClick={() => setAskBooking({ ...st, career_name: r.career_name })}
                          className="rounded-lg text-[11px] h-7 border-[#E5E7EB] text-[#4B5563] shrink-0"
                          data-testid={`ask-parents-btn-${st.booking_id}`}>
                          Ask more parents
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      <AskParentsDialog booking={askBooking} onOpenChange={() => setAskBooking(null)} onSent={load} />
    </div>
  );
}

// ─────────────────── COMING SOON PLACEHOLDER ───────────────────
function ComingSoonSection({ title, icon: Icon, blurb }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] px-6 py-14 text-center" data-testid="coming-soon">
      <div className="h-12 w-12 rounded-2xl bg-[#FFF0ED] flex items-center justify-center mx-auto mb-4">
        <Icon className="h-6 w-6 text-[#F97D6A]" />
      </div>
      <h2 className="text-base font-bold text-[#1F2937] font-['Outfit']">{title}</h2>
      <Badge className="border-0 bg-[#F3F4F6] text-[#6B7280] text-[10px] mt-2" data-testid="coming-soon-badge">Coming soon</Badge>
      <p className="text-sm text-[#6B7280] mt-3 max-w-sm mx-auto leading-relaxed">{blurb}</p>
    </div>
  );
}

// ─────────────────── SHARED FILTERED LIST VIEWS ───────────────────
const today = () => new Date().toISOString().split('T')[0];

export const LIST_SECTIONS = {
  tasks: {
    title: 'Pending Actions',
    blurb: 'Every experience waiting on you, across online sessions and workplace visits.',
    test: b => isActionable(b),
  },
  upcoming: {
    title: 'Upcoming Experiences',
    blurb: 'Assigned experiences still to happen — confirmed or awaiting the family.',
    test: b => ['confirmed', 'pending_parent_confirmation'].includes(b.status) && b.assigned_date && b.assigned_date >= today(),
  },
  completed: {
    title: 'Completed Experiences',
    blurb: 'Experiences that have been completed and closed off.',
    test: b => b.status === 'completed',
  },
  followup_overdue: {
    title: 'Waiting on the mentor to confirm',
    blurb: 'These dates have passed but the mentor has not confirmed what happened yet. Remind them, or close the item administratively if there is still no answer.',
    test: b => needsMentorConfirmation(b),
    action: 'Remind the mentor, or close administratively',
  },
  followup_feedback: {
    title: 'Awaiting family feedback',
    blurb: 'Completed experiences where the family has not left feedback yet. Students receive their certificate once feedback is in — send a reminder straight from the row.',
    test: b => needsFamilyFeedback(b),
    action: 'Chase feedback',
  },
  admin_closed: {
    title: 'Administratively closed',
    blurb: 'Unresolved experiences a coordinator closed off with a reason. No certificate was issued and attendance was left as recorded.',
    test: b => !!b.admin_closed_at,
  },
};

function ExperienceListSection({ def, bookings, bLoading, onRefresh, onOpenDetails, extras, mode }) {
  const rows = bookings.filter(def.test).filter(b => !mode
    || isOfflineMode(b.assigned_mode || b.requested_mode) === (mode === 'offline'));
  return (
    <div data-testid={`list-section-${def.key}`}>
      <div className="mb-4">
        <p className="text-sm text-[#6B7280] leading-relaxed max-w-2xl">{def.blurb}</p>
        <p className="text-xs text-[#9CA3AF] mt-1">
          <span data-testid="list-count">{rows.length}</span> record{rows.length === 1 ? '' : 's'}
          {mode ? ` · ${mode === 'offline' ? 'workplace visits' : 'online sessions'} only` : ''}
          {def.action ? ` · outstanding action: ${def.action}` : ''}
        </p>
      </div>
      {extras}
      <ExperienceTable bookings={rows} allBookings={bookings} loading={bLoading} onRefresh={onRefresh}
        offline={false} mixed hideFilters onOpenDetails={onOpenDetails}
        rowAction={def.key === 'followup_feedback'
          ? (b => <FeedbackReminderButton key={b.feedback_reminder_sent_at || b.id} booking={b} onSent={onRefresh} />)
          : undefined} />
    </div>
  );
}

// ─────────────────── EXPERIENCES SECTION ───────────────────
function ExperiencesSection({ mode, bookings, bLoading, onRefresh, deepLink, onNavigate, onOpenDetails }) {
  const offlineSection = mode === 'offline';
  const [subTab, setSubTab] = useState(deepLink?.tab || 'requests');
  const scoped = bookings.filter(b => isOfflineMode(b.assigned_mode || b.requested_mode) === offlineSection);
  const tabFade = useOverflowFade();
  const subTabs = [
    { id: 'requests', label: offlineSection ? 'Visit Requests' : 'Session Requests' },
    { id: 'reschedules', label: 'Reschedules' },
    { id: 'unfulfilled', label: 'Unfulfilled' },
  ];

  return (
    <div data-testid={`experiences-section-${mode}`}>
      <div className="flex items-center gap-2 mb-4">
        <div className={`relative flex-1 min-w-0 ${tabFade.faded ? "after:content-[''] after:absolute after:right-0 after:top-0 after:h-full after:w-8 after:bg-gradient-to-l after:from-[#F3F4F6] after:pointer-events-none" : ''}`}>
        <div ref={tabFade.ref} className="flex gap-1 bg-[#F3F4F6] rounded-lg p-1 overflow-x-auto [&::-webkit-scrollbar]:hidden">
          {subTabs.map(t => (
            <button key={t.id} onClick={() => setSubTab(t.id)} className={`px-3 min-h-[40px] sm:min-h-0 py-2.5 sm:py-1.5 text-xs font-semibold rounded-md transition-colors whitespace-nowrap ${subTab === t.id ? 'bg-white text-[#1F2937] shadow-sm' : 'text-[#6B7280] hover:text-[#1F2937]'}`} data-testid={`tab-exp-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>
        </div>
        <Button size="sm" variant="outline" onClick={() => onNavigate('calendar')} className="rounded-lg text-xs shrink-0 border-gray-200 px-2 sm:px-3" data-testid="go-school-calendar">
          <CalendarIcon className="h-3.5 w-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Calendar</span>
        </Button>
      </div>
      {subTab === 'requests' && (
        <ExperienceTable key={`${mode}-${deepLink?.filter || 'all'}`} bookings={scoped} allBookings={bookings}
          loading={bLoading} onRefresh={onRefresh} offline={offlineSection}
          initialFilter={deepLink?.filter} onOpenDetails={onOpenDetails} />
      )}
      {subTab === 'reschedules' && <RescheduleRequestsTab mode={mode} onRefresh={onRefresh} />}
      {subTab === 'unfulfilled' && <UnfulfilledRequestsTab mode={mode} />}
    </div>
  );
}

// ─────────────────── SIDEBAR NAVIGATION ───────────────────
export const EXPERIENCE_SECTIONS = [
  { id: 'exp_online', label: 'Online Sessions', icon: Video },
  { id: 'exp_visits', label: 'Workplace Visits', icon: Building2 },
  { id: 'exp_internships', label: 'Internships', icon: BriefcaseBusiness },
  { id: 'exp_shadowing', label: 'Job Shadowing', icon: Eye, soon: true },
  { id: 'exp_workshops', label: 'School Workshops', icon: Presentation, soon: true },
];

// School logo, or its initials when no logo has been uploaded
function SchoolMark({ school, size = 'h-14' }) {
  const logo = school?.school_logo_url || school?.logo_url;
  if (logo) {
    return <img src={logo} alt={school?.school_name || 'School'} className={`${size} w-auto max-w-full object-contain object-left`} data-testid="sidebar-school-logo" />;
  }
  const initials = (school?.school_name || 'S').split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
  return (
    <span className={`${size} aspect-square rounded-lg bg-[#FFF0ED] text-[#F97D6A] text-base font-bold flex items-center justify-center`} data-testid="sidebar-school-initials">
      {initials}
    </span>
  );
}

function Sidebar({ section, setSection, stats, counts, isMobile, onClose, school, safeguarding }) {
  const items = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard, badge: counts?.total },
    { id: 'students', label: 'Students', icon: GraduationCap, badge: stats?.pending_children },
    { id: 'mentors', label: 'Mentors', icon: Briefcase, badge: counts?.mentors },
    { id: 'coverage', label: 'Career Coverage', icon: Compass },
    { group: 'Experiences' },
    ...EXPERIENCE_SECTIONS.map(s => ({
      ...s,
      badge: s.id === 'exp_online' ? counts?.online
        : s.id === 'exp_visits' ? counts?.offline
          : s.id === 'exp_internships' ? counts?.internships : 0,
    })),
    ...(safeguarding ? [{ group: 'Student Safety' }, { id: 'safeguarding', label: 'Safeguarding', icon: ShieldCheck, badge: counts?.safeguarding }] : []),
    { group: 'Settings' },
    { id: 'settings', label: 'School Settings', icon: Settings },
    { id: 'tickets', label: 'Requests & Issues', icon: LifeBuoy },
  ];

  return (
    <nav className={`${isMobile ? 'p-4' : 'w-56 shrink-0 border-r border-[#E5E7EB] bg-white sticky top-0 h-screen'} flex flex-col`} data-testid="coordinator-sidebar">
      {!isMobile && (
        <div className="px-4 pt-5 pb-4" data-testid="sidebar-school">
          <SchoolMark school={school} />
          <p className="text-sm font-bold text-[#1F2937] font-['Outfit'] leading-tight mt-2.5" data-testid="sidebar-school-name">
            {school?.school_name || 'Your School'}
          </p>
          <p className="text-[10px] text-[#9CA3AF] mt-0.5">Career Coordinator</p>
        </div>
      )}
      <div className="flex-1 space-y-0.5 px-2 overflow-y-auto">
        {items.map(item => (
          item.group ? (
            <p key={item.group} className="px-3 pt-4 pb-1 text-xs font-bold text-[#9CA3AF] uppercase tracking-widest" data-testid={`nav-group-${item.group.toLowerCase()}`}>
              {item.group}
            </p>
          ) : (
            <button
              key={item.id}
              onClick={() => { setSection(item.id); if (isMobile && onClose) onClose(); }}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                section === item.id
                  ? 'bg-[#FFF0ED] text-[#F97D6A]'
                  : 'text-[#6B7280] hover:bg-[#F8F9FA] hover:text-[#1F2937]'
              }`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon className="h-4 w-4 shrink-0" />
              <span className="flex-1 text-left">{item.label}</span>
              {item.soon && (
                <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-[#F3F4F6] text-[#9CA3AF]">Soon</span>
              )}
              {!item.soon && item.badge > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#F97D6A] text-white">{item.badge}</span>
              )}
            </button>
          )
        ))}
      </div>
    </nav>
  );
}

// ─────────────────── MAIN DASHBOARD ───────────────────
const SECTION_TITLES = {
  overview: 'Overview',
  students: 'Students',
  exp_online: 'Online Sessions',
  exp_visits: 'Workplace Visits',
  exp_shadowing: 'Job Shadowing',
  exp_internships: 'Internships',
  exp_workshops: 'School Workshops',
  mentors: 'Mentors',
  coverage: 'Career Coverage',
  settings: 'School Settings',
  tickets: 'Requests & Issues',
  safeguarding: 'Safeguarding',
  calendar: 'School Calendar',
  tasks: 'Pending Actions',
  upcoming: 'Upcoming Experiences',
  completed: 'Completed Experiences',
  followup_overdue: 'Follow-ups',
  followup_feedback: 'Follow-ups',
  admin_closed: 'Administratively closed',
};

export default function CoordinatorDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [school, setSchool] = useState(null);
  const [params, setParams] = useSearchParams();
  const requestedSection = params.get('section') || location.state?.section;
  const aliased = requestedSection === 'experiences' ? 'exp_online' : requestedSection;
  const section = SECTION_TITLES[aliased] ? aliased : 'overview';
  const setSection = useCallback((id, extra) => {
    setParams(prev => {
      const p = new URLSearchParams(prev);
      p.set('section', id);
      ['ip', 'it', 'int', 'f', 'm', 'mode'].forEach(k => p.delete(k));   // never carry one section's state into another
      Object.entries(extra || {}).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k)));
      return p;
    });
  }, [setParams]);
  const unknownSection = Boolean(params.get('section')) && !SECTION_TITLES[aliased];
  useEffect(() => {
    if (unknownSection) {
      setParams(prev => {
        const p = new URLSearchParams(prev);
        p.set('section', 'overview');
        return p;
      }, { replace: true });
    }
  }, [unknownSection, setParams]);

  const [bookings, setBookings] = useState([]);
  const [bLoading, setBLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const fetchBase = useCallback(async () => {
    try {
      const [s, sc] = await Promise.all([api.get('/stats'), api.get('/schools')]);
      setStats(s.data);
      if (sc.data.length) setSchool(sc.data[0]);
    } catch {}
  }, []);

  const fetchBookings = useCallback(async () => {
    setBLoading(true);
    try { const r = await api.get('/bookings'); setBookings(r.data); } catch { setBookings([]); }
    setBLoading(false);
  }, []);

  const [interns, setInterns] = useState(null);
  const fetchInterns = useCallback(async () => {
    try { const r = await api.get('/coordinator/internship-dashboard'); setInterns(r.data); } catch { setInterns(null); }
  }, []);

  // Safeguarding counts exist only for coordinators holding the permission
  const [safeguardingAttention, setSafeguardingAttention] = useState(null);
  const fetchSafeguarding = useCallback(async () => {
    if (!user?.safeguarding_access) { setSafeguardingAttention(null); return; }
    try { const r = await api.get('/safeguarding/attention'); setSafeguardingAttention(r.data); }
    catch { setSafeguardingAttention(null); }
  }, [user?.safeguarding_access]);

  useEffect(() => { fetchBase(); fetchBookings(); fetchInterns(); }, [fetchBase, fetchBookings, fetchInterns]);
  // Re-read the counts on every section change, so badges stay in step after an action elsewhere
  useEffect(() => { fetchBase(); fetchSafeguarding(); }, [section, fetchBase, fetchSafeguarding]);

  const onRefresh = () => { fetchBookings(); fetchBase(); fetchInterns(); fetchSafeguarding(); };
  const attention = buildAttention({ bookings, interns, stats });
  const [deepLink, setDeepLink] = useState(null);
  const [detailBooking, setDetailBooking] = useState(null);
  const [assignFromDetails, setAssignFromDetails] = useState(null);
  const [rescheduleFromDetails, setRescheduleFromDetails] = useState(null);
  const goToSection = (id, opts) => {
    setDeepLink(opts || null);
    setSection(id === 'experiences' ? 'exp_online' : id, opts?.url);
  };

  const pendingCounts = {
    ...attention,
    safeguarding: safeguardingAttention?.count || 0,
    total: attention.total + (safeguardingAttention?.count || 0),
  };

  const isExperienceList = section === 'exp_online' || section === 'exp_visits';

  return (
    <div className="min-h-screen lg:min-h-[calc(100vh-64px)] bg-[#F8F9FA] flex flex-col lg:flex-row" data-testid="coordinator-dashboard">
      <CoordinatorAcknowledgments onDone={() => { fetchBase(); fetchBookings(); fetchSafeguarding(); }} />

      {/* Desktop sidebar */}
      <div className="hidden lg:block">
        <Sidebar section={section} setSection={goToSection} stats={stats} counts={pendingCounts} isMobile={false} school={school} safeguarding={!!user?.safeguarding_access} />
      </div>

      {/* Mobile header */}
      <div className="lg:hidden sticky top-0 z-40 flex items-center justify-between gap-2 px-3 py-2 bg-white border-b border-[#E5E7EB]">
        <button onClick={() => setMobileMenuOpen(true)} className="h-11 w-11 -ml-1 flex items-center justify-center rounded-lg hover:bg-[#F3F4F6]" data-testid="mobile-menu-btn" aria-label="Open menu">
          <Menu className="h-5 w-5 text-[#4B5563]" />
        </button>
        <span className="text-sm font-semibold text-[#1F2937] font-['Outfit'] truncate">{SECTION_TITLES[section]}</span>
        <span className="text-xs text-[#9CA3AF] truncate max-w-[30%] text-right">{user?.full_name || user?.email}</span>
      </div>

      {/* Mobile sidebar drawer */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/30" onClick={() => setMobileMenuOpen(false)} />
          <div className="fixed left-0 top-0 h-full w-64 bg-white shadow-xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7EB]">
              <div className="flex items-center gap-2 min-w-0">
                <SchoolMark school={school} size="h-9" />
                <p className="text-sm font-bold text-[#1F2937] font-['Outfit'] truncate">{school?.school_name || 'Your School'}</p>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="h-11 w-11 -mr-2 flex items-center justify-center shrink-0 rounded-lg hover:bg-[#F3F4F6]" aria-label="Close menu" data-testid="mobile-menu-close"><X className="h-5 w-5 text-[#9CA3AF]" /></button>
            </div>
            <div className="h-[calc(100%-61px)] flex flex-col">
              <div className="flex-1 overflow-y-auto">
                <Sidebar section={section} setSection={goToSection} stats={stats} counts={pendingCounts} isMobile={true} school={school} safeguarding={!!user?.safeguarding_access} onClose={() => setMobileMenuOpen(false)} />
              </div>
              <div className="border-t border-[#E5E7EB] p-2 space-y-1 bg-white">
                <div className="px-2 py-1 text-xs text-[#9CA3AF] truncate">{user?.full_name || user?.email}</div>
                <button onClick={() => { setMobileMenuOpen(false); navigate('/'); }} className="w-full h-11 flex items-center gap-2 px-2 rounded-lg text-sm text-[#4B5563] hover:bg-[#F3F4F6]" data-testid="mobile-home-btn">
                  <Home className="h-4 w-4" /> Home
                </button>
                <button onClick={() => { setMobileMenuOpen(false); logout(); }} className="w-full h-11 flex items-center gap-2 px-2 rounded-lg text-sm text-[#DC2626] hover:bg-[#FEF2F2]" data-testid="mobile-logout-btn">
                  <LogOut className="h-4 w-4" /> Log out
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 p-4 pb-24 sm:p-6 lg:p-8 lg:pb-8 overflow-y-auto">
        <div className="max-w-6xl mx-auto">
          {/* Section header (desktop only, non-overview) */}
          {section !== 'overview' && section !== 'calendar' && (
            <div className="hidden lg:flex items-center justify-between mb-6">
              <h1 className="text-xl font-bold text-[#1F2937] font-['Outfit'] tracking-tight">{SECTION_TITLES[section]}</h1>
              {isExperienceList && (
                <Button variant="ghost" size="sm" onClick={onRefresh} className="text-xs rounded-lg text-[#6B7280] hover:bg-white"><RefreshCw className="h-3.5 w-3.5 mr-1" />Refresh</Button>
              )}
            </div>
          )}

          {section === 'overview' && <OverviewTab stats={stats} school={school} bookings={bookings} user={user} onNavigate={goToSection} attention={pendingCounts} interns={interns} safeguarding={safeguardingAttention} />}
          {section === 'students' && <StudentsTab />}
          {section === 'exp_online' && <ExperiencesSection key={`online-${deepLink?.tab || ''}-${deepLink?.filter || ''}`} mode="online" bookings={bookings} bLoading={bLoading} onRefresh={onRefresh} deepLink={deepLink} onNavigate={goToSection} onOpenDetails={setDetailBooking} />}
          {section === 'exp_visits' && <ExperiencesSection key={`offline-${deepLink?.tab || ''}-${deepLink?.filter || ''}`} mode="offline" bookings={bookings} bLoading={bLoading} onRefresh={onRefresh} deepLink={deepLink} onNavigate={goToSection} onOpenDetails={setDetailBooking} />}
          {section === 'calendar' && <SchoolCalendar bookings={bookings} onRefresh={onRefresh} onOpenDetails={setDetailBooking} />}
          {LIST_SECTIONS[section] && (
            <ExperienceListSection def={{ ...LIST_SECTIONS[section], key: section }} bookings={bookings}
              mode={params.get('mode') || deepLink?.mode}
              bLoading={bLoading} onRefresh={onRefresh} onOpenDetails={setDetailBooking}
              extras={['tasks', 'upcoming', 'completed'].includes(section) ? (
                <>
                  {section === 'tasks' && stats?.pending_mentors > 0 && (
                    <Button size="sm" variant="outline" className="rounded-lg text-xs border-gray-200 mb-4" onClick={() => goToSection('mentors')} data-testid="task-mentors">
                      {stats.pending_mentors} mentor application{stats.pending_mentors === 1 ? '' : 's'} to approve
                    </Button>
                  )}
                  <InternshipListExtras kind={section} onNavigate={goToSection} />
                </>
              ) : null} />
          )}
          {section === 'exp_shadowing' && <ComingSoonSection title="Job Shadowing" icon={Eye} blurb="Students shadowing a mentor through a real working day. We will bring scheduling, consent and supervision for shadowing here once the workflow is agreed." />}
          {section === 'exp_internships' && <InternshipsSection initialTab={deepLink?.tab} />}
          {section === 'exp_workshops' && <ComingSoonSection title="School Workshops" icon={Presentation} blurb="Mentor-led sessions hosted at your school for a whole class or year group, with room booking and attendance lists." />}
          {section === 'coverage' && <CareerCoverage key={deepLink?.gap || 'all'} initialGap={deepLink?.gap} />}
          {section === 'mentors' && <MentorApplicationsTab />}
          {section === 'safeguarding' && <SafeguardingSection school={school} openReportId={deepLink?.reportId} onChanged={fetchSafeguarding} />}
          {section === 'settings' && <SchoolSettingsTab school={school} />}
          {section === 'tickets' && <RequestsAndIssues />}
        </div>
      </main>

      <ReportIssueButton sidebar />
      {detailBooking && (
        <ExperienceDetailsDialog booking={detailBooking} bookings={bookings}
          onClose={() => setDetailBooking(null)} onRefresh={onRefresh}
          onAssign={setAssignFromDetails} onReschedule={setRescheduleFromDetails} />
      )}
      {assignFromDetails && (
        <AssignMentorDialog booking={assignFromDetails} onClose={() => setAssignFromDetails(null)}
          onAssigned={() => { setAssignFromDetails(null); onRefresh(); }} />
      )}
      {rescheduleFromDetails && (
        <AssignMentorDialog mode="reschedule" booking={rescheduleFromDetails} onClose={() => setRescheduleFromDetails(null)}
          onAssigned={() => { setRescheduleFromDetails(null); onRefresh(); }} />
      )}
    </div>
  );
}
